import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { SharedKernel } from '@/shell';
import { useSession, demoFlag, historyHasBid, recordMockPlacement } from '@/shell';
import {
  ApiError,
  MAX_BID_AMOUNT_CENTS,
  type BidPlacementResult,
  type BidVoidedFrame,
  type PublicBidEntry,
  type PublicItemDetail,
  type WsClientFrame,
} from '@neon-one/auctions-types';

import { bidApi } from './api';
// FIXME: cross-feature import — catalog sync on bid placement. Move to
// an event bus or shell-level coordination layer once the feature boundary
// rules are enforced at build time.
import { useCatalogStore } from '@/features/catalog';

export type Kernel = SharedKernel;

// Matches a WS frame's target against the currently-open item/bundle detail.
// `itemId`/`bundleId` are independent PK spaces that can numerically
// collide, so this must compare against the matching field rather than a
// single ambiguous id (see PublicItemDetail's itemId/bundleId comment).
function matchesDetail(
  d: PublicItemDetail,
  payload: { itemId?: number | null; bundleId?: number | null },
): boolean {
  return payload.itemId != null ? payload.itemId === d.itemId : payload.bundleId === d.bundleId;
}

type BidType = 'standard' | 'max';
type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
export type BidTargetKind = 'item' | 'bundle';

// Lane C1 — extension to the server response for the locked
// max-tie design. When two bidders set the same max amount, the
// engine accepts both, runs them to the tied amount, and times-
// orders the first submission as winner. The submitter (second
// bidder) is outbid immediately; the BidSheet stays open and
// swaps to a result panel.
//
// Mock-only this design pass. Wire change is admin-side and
// deferred (collision matrix in bidder-design-guide §10).
export interface TieResult {
  tiedAtCents: number;
  suggestedNextCents: number;
}

export const useBidStore = defineStore('bid', () => {
  const slug = ref<string | null>(null);
  const itemId = ref<number | null>(null);
  // Lane B7 — discriminator for bundle-mode bidding. `itemId` continues
  // to hold whichever numeric id is being bid on (item OR bundle PK —
  // the two id spaces are independent); `kind` says which one. Kept as
  // a sibling ref (not folded into `itemId`) so existing call sites that
  // only care about "the id" don't need touching.
  const kind = ref<BidTargetKind>('item');
  const detail = ref<PublicItemDetail | null>(null);
  const detailState = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
  const amountCents = ref<number>(0);
  const type = ref<BidType>('standard');
  const state = ref<SubmitState>('idle');
  const errorCode = ref<string | null>(null);
  const errorMessage = ref<string | null>(null);
  const errorMinimumCents = ref<number | null>(null);
  const errorCurrentHighCents = ref<number | null>(null);
  const lastResult = ref<BidPlacementResult | null>(null);
  // Lane C1 — populated when the most recent placement came back with
  // an immediate-outbid tie. BidSheet keys its result-panel mode off
  // this; clearing it (via clearTieResult or setItem) returns the
  // sheet to form mode.
  const tieResult = ref<TieResult | null>(null);
  const maxExceeded = ref(false);

  const isMaxBid = computed(() => type.value === 'max');
  const amountExceedsMax = computed(() => amountCents.value > MAX_BID_AMOUNT_CENTS);

  // GT1-395 — is the viewer currently the high bidder on this item/bundle?
  // `bidHistory` is server-ordered most-recent-first, so the leading bid is
  // always index 0. Each entry carries a server-computed `isMine`, so this
  // is a pure read of already-fetched data — no extra endpoint needed.
  // Used to disable "Standard bid" (overbidding yourself is a no-op the UI
  // shouldn't offer); "Max bid" stays enabled since raising your own proxy
  // ceiling while leading is legitimate.
  const viewerIsHighBidder = computed(() => detail.value?.bidHistory?.[0]?.isMine === true);

  async function loadDetail(eventSlug: string, id: number, targetKind: BidTargetKind = 'item'): Promise<void> {
    slug.value = eventSlug;
    detailState.value = 'loading';
    try {
      const d = targetKind === 'bundle'
        ? await bidApi.bundleDetail(eventSlug, id)
        : await bidApi.itemDetail(eventSlug, id);
      detail.value = d;
      setItem(d.id, d.minimumNextBidCents, targetKind);
      detailState.value = 'success';
    } catch {
      detailState.value = 'error';
    }
  }

  // Re-fetches item/bundle detail without resetting form state. Called
  // after a bid rejection to sync the displayed bid status with server
  // reality before the WS item.high_bid_changed frame arrives.
  async function refreshDetail(): Promise<void> {
    if (!slug.value || !itemId.value) return;
    try {
      detail.value = kind.value === 'bundle'
        ? await bidApi.bundleDetail(slug.value, itemId.value)
        : await bidApi.itemDetail(slug.value, itemId.value);
    } catch {
      // silent — WS frame will reconcile if the fetch fails
    }
  }

  function setItem(id: number, minimumNextBidCents: number, targetKind: BidTargetKind = 'item'): void {
    itemId.value = id;
    kind.value = targetKind;
    amountCents.value = minimumNextBidCents;
    type.value = 'standard';
    state.value = 'idle';
    errorCode.value = null;
    errorMessage.value = null;
    errorMinimumCents.value = null;
    errorCurrentHighCents.value = null;
    lastResult.value = null;
    tieResult.value = null;
    maxExceeded.value = false;
  }

  function clearTieResult(): void {
    tieResult.value = null;
  }

  /**
   * Lane C1 — reset the sheet back to form mode after a tie result,
   * pre-filling the amount input with the engine's suggested next bid.
   * Called from BidSheet's "Raise to $X" button.
   */
  function resetForRaise(suggestedCents: number): void {
    amountCents.value = suggestedCents;
    state.value = 'idle';
    tieResult.value = null;
    errorCode.value = null;
    errorMessage.value = null;
  }

  function setAmount(value: number): void {
    amountCents.value = Math.max(0, Math.round(value));
    errorCode.value = null;
    errorMessage.value = null;
  }

  function setType(next: BidType): void {
    type.value = next;
    if (next === 'max' && detail.value?.myMaxAmountCents != null) {
      amountCents.value = Math.max(
        detail.value.myMaxAmountCents + detail.value.bidIncrementCents,
        detail.value.minimumNextBidCents,
      );
    }
  }

  function newIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `bid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function applyApiError(err: ApiError): void {
    state.value = 'error';
    errorCode.value = err.code;
    errorMessage.value = mapErrorCopy(err);
    errorMinimumCents.value = err.body.currentMinimum ?? null;
    errorCurrentHighCents.value = err.body.currentHighBid ?? null;
  }

  async function submit(): Promise<boolean> {
    if (itemId.value === null || state.value === 'submitting') return false;
    if (amountExceedsMax.value) {
      state.value = 'error';
      errorCode.value = 'BID_TOO_HIGH';
      errorMessage.value = formatBidTooHighCopy();
      return false;
    }
    state.value = 'submitting';
    errorCode.value = null;
    errorMessage.value = null;

    // Mock-auth short-circuit: the prototype-issued JWT can't authenticate
    // against the real /bids endpoint, so we fabricate a successful
    // placement result locally and update the cached item so the catalog
    // card / item-detail view reflect the new high bid immediately.
    const session = useSession();
    if (session.jwt?.startsWith('mock-jwt-')) {
      // Snapshot the reactive target/amount before the artificial delay —
      // the type selector stays enabled and the target can change while this
      // await is in flight, so re-reading `.value` afterward could attach
      // the mock placement to a different entity or bid type than the one
      // actually submitted.
      const submittedKind = kind.value;
      const submittedItemId = itemId.value;
      const submittedType = type.value;
      const submittedAmountCents = amountCents.value;
      await new Promise((r) => setTimeout(r, 350));
      const item = detail.value;

      // Lane C1 — `?demo=max-tie` flips the next placement to return
      // the engine's "outbid immediately" shape. The amount the bidder
      // tried to set becomes the tied amount; the suggested raise is
      // tied + one increment.
      const demo = demoFlag();
      const isTieDemo = demo === 'max-tie' && submittedType === 'max';
      const tiedAtCents = submittedAmountCents;
      const increment = item?.bidIncrementCents ?? 2500;
      const suggestedNextCents = tiedAtCents + increment;

      const result: BidPlacementResult = {
        bid: {
          id: Date.now(),
          auctionEventId: 0,
          auctionItemId: submittedKind === 'item' ? submittedItemId : null,
          bundleId: submittedKind === 'bundle' ? submittedItemId : null,
          bidderId: 0,
          amountCents: submittedAmountCents,
          bidType: submittedType,
          maxAmountCents: submittedType === 'max' ? submittedAmountCents : null,
          voided: false,
          voidReason: null,
          voidedByAdminId: null,
          voidedAt: null,
          placedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        },
        item: null,
        bundle: null,
        bidCount: (item?.bidCount ?? 0) + 1,
      };
      if (item) {
        // GT1-395 — this short-circuit never fires a real WS bid.placed
        // frame (there's no backend to broadcast one), so it's the only
        // path that can keep bidHistory[0] in sync for mock/demo sessions.
        // Without it, viewerIsHighBidder (bidHistory[0].isMine) stays false
        // after the very first bid, and the generic max-bid explainer shows
        // instead of the "you're leading" hint. Tie-demo is the exception:
        // the design there is "immediately outbid", so the viewer is not
        // marked as leading.
        const newBidHistoryEntry: PublicBidEntry = {
          amountCents: tiedAtCents,
          bidderHandle: isTieDemo ? 'Another bidder' : 'You',
          placedAt: result.bid.placedAt,
          isMine: !isTieDemo,
        };
        // Tie case: the displayed current bid is the tied amount (engine
        // ran both bids up to it) — same as a non-tie placement at this
        // value, just framed differently to the bidder.
        detail.value = {
          ...item,
          currentHighBidCents: tiedAtCents,
          bidCount: (item.bidCount ?? 0) + 1,
          bidHistory: [newBidHistoryEntry, ...(item.bidHistory ?? [])].slice(0, 10),
          // GT1-395 — without this, myMaxAmountCents (which the "you're
          // leading" hint reads to choose "Set" vs "Raise" your max bid)
          // stays stale after a successful max bid until a full reload.
          ...(submittedType === 'max' ? { myMaxAmountCents: result.bid.maxAmountCents } : {}),
        };
      }
      // Record the placement so the mock /me/bids overlay can report this
      // entity as winning — demo sessions have no backend to echo it back,
      // and without it the item-detail bar and My Bids stay on the static
      // fixture after a bid. Tie-demo is excluded: its design is
      // "immediately outbid", so the bidder is not winning.
      if (!isTieDemo) {
        recordMockPlacement({
          itemId: submittedKind === 'item' ? submittedItemId : null,
          bundleId: submittedKind === 'bundle' ? submittedItemId : null,
          title: item?.title ?? 'Auction item',
          amountCents: tiedAtCents,
          maxAmountCents: submittedType === 'max' ? tiedAtCents : null,
        });
      }
      // Reflect the new high bid into the catalog store too so the card
      // on the catalog grid / bundle row / item-detail view all refresh.
      // Frame matches on `itemId ?? bundleId`, so only one is populated.
      const catalog = useCatalogStore();
      catalog.applyFrame({
        type: 'bid.placed',
        payload: {
          itemId: submittedKind === 'item' ? submittedItemId : null,
          bundleId: submittedKind === 'bundle' ? submittedItemId : null,
          currentHighBidCents: tiedAtCents,
          amountCents: tiedAtCents,
          bidCount: (item?.bidCount ?? 0) + 1,
        },
        // The frame schema has more fields, but applyFrame only reads
        // type + payload — cast through unknown to keep TS happy.
      } as unknown as Parameters<typeof catalog.applyFrame>[0]);
      lastResult.value = result;
      tieResult.value = isTieDemo ? { tiedAtCents, suggestedNextCents } : null;
      maxExceeded.value = false;
      state.value = 'success';
      return true;
    }

    // GT1-395 — pin the type for the rest of this call. `type` is reactive and
    // the viewerIsHighBidder watcher in BidSheet/InlineBidForm flips it to
    // 'max' the moment this bid's own WS frame marks the viewer as leading,
    // which can land while the await below is still in flight. The branches
    // after it must reflect the bid that was actually submitted.
    const submittedType = type.value;
    try {
      const result = await bidApi.place(
        kind.value === 'bundle'
          ? { bundleId: itemId.value, amountCents: amountCents.value, type: submittedType }
          : { itemId: itemId.value, amountCents: amountCents.value, type: submittedType },
        newIdempotencyKey(),
      );
      lastResult.value = result;
      state.value = 'success';
      maxExceeded.value = false;
      // GT1-380 — set (never increment) bidCount from the server's
      // authoritative post-commit value. A WS frame for this same bid
      // commonly reconciles bidCount on `detail.value` before this await
      // resolves; incrementing on top of that double-counted. Setting from
      // `result.bidCount` is idempotent regardless of arrival order, and
      // applies to both standard and max bids since the count itself
      // (unlike the price) never depends on the proxy ladder outcome.
      if (detail.value) {
        detail.value.bidCount = result.bidCount;
      }
      // GT1-395 — mirrors the standard-bid optimistic update below, but for
      // myMaxAmountCents: without this the "you're leading" hint keeps
      // showing "Set a max bid" (as if none exists) after a successful max
      // bid, until a full reload re-fetches detail.
      if (submittedType === 'max' && detail.value) {
        detail.value.myMaxAmountCents = result.bid.maxAmountCents;
      }
      // Optimistic catalog update for standard bids only — the placed amount
      // IS the new high bid for standard. For max bids the server may place
      // a lower proxy step; let the WS frame reconcile instead.
      if (submittedType === 'standard' && detail.value) {
        const newHighCents = result.item?.currentHighBidCents ?? result.bundle?.currentHighBidCents ?? amountCents.value;
        const newMin = newHighCents + detail.value.bidIncrementCents;
        detail.value.currentHighBidCents = newHighCents;
        detail.value.minimumNextBidCents = newMin;
        amountCents.value = newMin;
        const catalog = useCatalogStore();
        catalog.applyFrame({
          type: 'bid.placed',
          payload: {
            itemId: kind.value === 'item' ? itemId.value : null,
            bundleId: kind.value === 'bundle' ? itemId.value : null,
            currentHighBidCents: newHighCents,
            amountCents: newHighCents,
            bidCount: result.bidCount,
          },
        } as unknown as WsClientFrame);
      }
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        applyApiError(e);
        // Re-fetch so the bid sheet shows the actual current bid, not the
        // stale value. WS item.high_bid_changed will also arrive, but this
        // fires immediately so there's no visible stale state window.
        if (e.code === 'OUTBID' || e.code === 'BID_TOO_LOW' || e.code === 'ALREADY_HIGH_BIDDER') {
          void refreshDetail();
        }
      } else {
        state.value = 'error';
        errorMessage.value = 'We couldn\'t place your bid. Try again.';
      }
      return false;
    }
  }

  function applyFrame(frame: WsClientFrame): void {
    if (!detail.value) return;
    if (frame.type === 'item.status_changed') {
      const payload = frame.payload as {
        itemId?: number | null;
        bundleId?: number | null;
        status: PublicItemDetail['status'];
        closedAt?: string | null;
      } | undefined;
      if (!payload || !matchesDetail(detail.value, payload)) return;
      detail.value.status = payload.status;
      if (payload.closedAt !== undefined) detail.value.closedAt = payload.closedAt ?? null;
    }
    if (frame.type === 'bid.placed') {
      // GT1-395 — bidHistory[0].isMine drives viewerIsHighBidder, so without
      // this the Standard option stays (in)correctly enabled/disabled after
      // the viewer's own bid, or after someone else outbids them, until a
      // full reload re-fetches bidHistory. `bidderUuid` here is whoever
      // pressed submit, which is NOT the high bidder when a competing max
      // auto-defends -- item.high_bid_changed below carries the authoritative
      // currentHighBidderUuid and corrects isMine on this entry. Only
      // bidHistory is touched here -- currentHighBidCents/minimumNextBidCents
      // stay owned by item.high_bid_changed, which carries the final
      // proxy-resolved value (see PublicBidEntry.currentHighBidCents).
      // A history row shows the amount of ITS OWN bid (payload.amountCents),
      // never payload.currentHighBidCents: a proxy auto-bid emits its own
      // frame carrying the same currentHighBidCents as the bid that triggered
      // it, so reading that field renders both rows at the final price.
      const payload = frame.payload as {
        itemId?: number | null;
        bundleId?: number | null;
        bidId?: number;
        bidderUuid?: string;
        bidderDisplay: string;
        amountCents: number;
        currentHighBidCents?: number;
        placedAt: string;
      } | undefined;
      if (!payload || !matchesDetail(detail.value, payload)) return;
      // GT1-469 — fan-out moved onto SQS + a worker, so this frame can arrive
      // after refreshDetail() already fetched history containing the same bid.
      // Dedup on bidId; the opposite order needs no handling because
      // refreshDetail replaces bidHistory wholesale.
      if (historyHasBid(detail.value.bidHistory, payload.bidId)) return;
      const session = useSession();
      const newEntry: PublicBidEntry = {
        ...(payload.bidId != null ? { bidId: payload.bidId } : {}),
        amountCents: payload.amountCents,
        bidderHandle: payload.bidderDisplay,
        placedAt: payload.placedAt,
        isMine: payload.bidderUuid != null && payload.bidderUuid === session.bidder?.uuid,
      };
      detail.value.bidHistory = [newEntry, ...detail.value.bidHistory].slice(0, 10);
    }
    if (frame.type === 'item.high_bid_changed') {
      const payload = frame.payload as {
        itemId?: number | null;
        bundleId?: number | null;
        currentHighBidCents: number;
        currentHighBidderUuid?: string | null;
        bidCount: number;
      } | undefined;
      if (!payload || !matchesDetail(detail.value, payload)) return;
      detail.value.currentHighBidCents = payload.currentHighBidCents;
      detail.value.bidCount = payload.bidCount;
      const newMin = payload.currentHighBidCents + detail.value.bidIncrementCents;
      detail.value.minimumNextBidCents = newMin;
      if (amountCents.value < newMin) amountCents.value = newMin;
      // GT1-395 — this frame is the only authority on *who* holds the high
      // bid after the proxy ladder resolves. bid.placed above names the
      // bidder who pressed submit, which is a different person whenever a
      // competing max auto-defends: it would mark the outbid challenger as
      // leading. Correct bidHistory[0] here (it always describes the current
      // high bid, and this frame arrives right after bid.placed).
      if (payload.currentHighBidderUuid !== undefined && detail.value.bidHistory.length > 0) {
        const session = useSession();
        const firstEntry = detail.value.bidHistory[0];
        if (firstEntry) {
          firstEntry.isMine =
            payload.currentHighBidderUuid != null &&
            payload.currentHighBidderUuid === session.bidder?.uuid;
        }
      }
    }
    if (frame.type === 'bid.voided') {
      const payload = frame.payload as BidVoidedFrame['payload'] | undefined;
      if (!payload) return;
      // Reject before matchesDetail(): with itemId AND bundleId both null,
      // matchesDetail falls through to `payload.bundleId === d.bundleId`,
      // i.e. `null === d.bundleId` — true for ANY open item detail (an item
      // always carries bundleId: null), so a malformed/targetless frame
      // would silently overwrite whichever item the bidder happens to have
      // open.
      if (payload.itemId == null && payload.bundleId == null) return;
      if (!matchesDetail(detail.value, payload) || payload.recalculatedHighBidCents == null) return;
      // GT1-442 — a void invalidates whatever amount this form suggested
      // against the pre-void price, in EITHER direction: unlike
      // item.high_bid_changed (always a competing bid, so only ever an
      // upward correction), voiding the current high bid can leave the new
      // minimum lower than what's currently typed. Always reset to the new
      // minimum rather than only bumping up.
      detail.value.currentHighBidCents = payload.recalculatedHighBidCents;
      const newMin = payload.recalculatedHighBidCents + detail.value.bidIncrementCents;
      detail.value.minimumNextBidCents = newMin;
      amountCents.value = newMin;
      // Drop the voided rows from bidHistory so viewerIsHighBidder
      // (bidHistory[0].isMine) reflects whoever now actually leads —
      // otherwise a voided entry left on top keeps the wrong bidder pinned
      // as "leading" (or the actual new leader hidden behind a still-enabled
      // Standard button) until a full reload replaces bidHistory wholesale.
      const voidedIds = payload.voidedBidIds;
      if (voidedIds && voidedIds.length > 0) {
        detail.value.bidHistory = detail.value.bidHistory.filter(
          (h) => h.bidId == null || !voidedIds.includes(h.bidId),
        );
      }
    }
    if (frame.type === 'bid.max_exceeded') {
      const payload = frame.payload as {
        itemId: number | null;
        bundleId?: number | null;
        currentHighBidCents: number;
      } | undefined;
      if (!payload || !matchesDetail(detail.value, payload)) return;
      detail.value.currentHighBidCents = payload.currentHighBidCents;
      maxExceeded.value = true;
      type.value = 'max';
    }
  }

  return {
    slug,
    itemId,
    kind,
    detail,
    detailState,
    amountCents,
    type,
    state,
    errorCode,
    errorMessage,
    errorMinimumCents,
    errorCurrentHighCents,
    lastResult,
    tieResult,
    maxExceeded,
    isMaxBid,
    amountExceedsMax,
    viewerIsHighBidder,
    loadDetail,
    refreshDetail,
    setItem,
    setAmount,
    setType,
    submit,
    clearTieResult,
    resetForRaise,
    applyFrame,
  };
});

function formatBidTooHighCopy(): string {
  return `Bids cannot exceed ${(MAX_BID_AMOUNT_CENTS / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 })}.`;
}

function mapErrorCopy(err: ApiError): string {
  switch (err.code) {
    case 'BID_TOO_HIGH':
      return formatBidTooHighCopy();
    case 'BID_TOO_LOW':
      return err.body.currentMinimum != null
        ? `Bid below the minimum. The next minimum is ${(err.body.currentMinimum / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 })}.`
        : 'Bid is below the next minimum. Try a higher amount.';
    case 'MAX_BID_LOWER':
      return 'Your max is already higher than that. Enter an amount above your current max.';
    case 'OUTBID':
      return err.body.currentHighBid != null
        ? `Someone bid first. The current high is ${(err.body.currentHighBid / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 })}.`
        : 'Someone bid first. Try again.';
    case 'ALREADY_HIGH_BIDDER':
      return "You're already the highest bidder. Set a max bid if you'd like to raise your ceiling.";
    case 'ITEM_CLOSED':
      return 'This item just closed. You can no longer place a bid.';
    case 'EVENT_PAUSED':
      return 'Bidding is paused. Try again when the auction resumes.';
    case 'BIDDER_NOT_ACTIVATED':
      return 'You need to finish activation before bidding.';
    // Reached only when the retry in usePlaceBid also fails (the bidder
    // dismissed the drawer, or declined the box). The first rejection is
    // handled there and never surfaces as copy.
    case 'TERMS_NOT_ACCEPTED':
      return 'This auction added terms and conditions. Accept them to keep bidding.';
    case 'PROXY_BIDDING_DISABLED':
      return 'Max bidding is disabled for this auction. Place a standard bid instead.';
    case 'RATE_LIMITED':
      return 'Hold on a moment. Too many bid attempts. Try again shortly.';
    default:
      return 'We couldn\'t place your bid. Try again.';
  }
}
